import { useApi } from "./api";

export interface SyntheticWorker {
  id: string;
  name: string;
  run_mode: "ON_DEMAND" | "AUTOMATIC";
  schedule: string | null;
  avatar: string | null;
  created_at: string | null;
  updated_at: string | null;
  active: boolean;
  summary: string | null;
  features: string[] | null;
}

export interface ChatMessage {
  id: string;
  role: string; // e.g., 'user' or 'assistant'
  content: string;
  timestamp: string;
  updates?: Record<string, unknown>;
}

interface CreateWorkerRequest extends Record<string, unknown> {
  name: string;
  avatar?: string;
  // Add other required fields for worker creation
}

interface UpdateWorkerRequest extends Record<string, unknown> {
  name?: string;
  status?: string;
  // Add other optional fields for worker update
}

interface CreateWorkerResponse {
  worker_id: string;
}

interface RunWorkerResponse {
  success: boolean;
  message: string;
  work_history_id: string;
}

export function useSyntheticWorkerService() {
  const api = useApi();

  return {
    /**
     * Get all synthetic workers
     */
    async getWorkers(): Promise<SyntheticWorker[]> {
      return api.get<SyntheticWorker[]>("/bff/synthetic-workers/");
    },

    /**
     * Get a single synthetic worker by ID
     */
    async getWorker(id: string): Promise<SyntheticWorker> {
      return api.get<SyntheticWorker>(`/bff/synthetic-workers/${id}`);
    },

    /**
     * Create a new synthetic worker
     */
    async createWorker(
      data: CreateWorkerRequest
    ): Promise<CreateWorkerResponse> {
      return api.post<CreateWorkerResponse>(
        "/bff/synthetic-workers/create",
        data
      );
    },

    /**
     * Update a synthetic worker
     */
    async updateWorker(
      id: string,
      data: UpdateWorkerRequest
    ): Promise<SyntheticWorker> {
      return api.put<SyntheticWorker>(`/bff/synthetic-workers/${id}`, data);
    },

    /**
     * Delete a synthetic worker
     */
    async deleteWorker(id: string): Promise<void> {
      return api.delete(`/bff/synthetic-workers/${id}`);
    },

    /**
     * Get chat history for a synthetic worker
     */
    async getChatHistory(workerId: string): Promise<ChatMessage[]> {
      const response = await api.get<{ messages: ChatMessage[] }>(
        `/bff/synthetic-workers/${workerId}/chat-history`
      );
      return response.messages;
    },

    /**
     * Send a message to the synthetic worker chat
     */
    async sendMessage(workerId: string, message: string): Promise<ChatMessage> {
      const response = await api.post<{
        message: string;
        updates?: Record<string, unknown>;
      }>(`/bff/synthetic-workers/${workerId}/chat`, { message });
      return {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: response.message,
        timestamp: new Date().toISOString(),
        updates: response.updates,
      };
    },

    /**
     * Execute a synthetic worker
     */
    async runWorker(workerId: string): Promise<RunWorkerResponse> {
      return api.post<RunWorkerResponse>(
        `/bff/synthetic-workers/${workerId}/execute`,
        {}
      );
    },
  };
}
